import cn from 'classnames';
import kebabCase from 'lodash/kebabCase';
import uniqueId from 'lodash/uniqueId';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import ICircle from 'components/icons/ICircle';
import { A11yStateContext } from 'context/A11yStateContext';
import useClickOutside from 'hooks/useClickOutside';
import useTooltipPosition from 'hooks/useTooltipPosition';
import style from 'styles/shared/tooltip';

const uniqueKebabId = (slug, id) => `${slug}-${uniqueId(kebabCase(id))}`;
const triggerButtonComponent = <ICircle className="mb-0.5 h-4 w-4 align-middle" />;
export const POSITION_TYPES = {
  bottom: 'bottom',
  top: 'top',
  overview: 'overviewPosition',
  mobileOrderSummary: 'mobileOrderSummary',
};

const Tooltip = ({
  centered = false,
  clamp = null,
  containerClass = '',
  position = POSITION_TYPES.top,
  tooltipClass = '',
  triggerButtonClass = '',
  triggerButtonContent = triggerButtonComponent,
  trackArrow = false,
  ariaId,
  children,
  ...restProps
}) => {
  const uniqueAriaId = useMemo(() => uniqueKebabId('tooltip', ariaId), [ariaId]);
  const containerRef = useRef();
  const transitionRef = useRef();
  const [activeTooltipId, setActiveTooltipId] = useContext(A11yStateContext);

  const [contentVisible, setContentVisible] = useState(false);
  const tooltipStyle = useTooltipPosition({
    contentVisible,
    trackArrow,
    tooltipRef: transitionRef,
    containerRef,
    clamp,
  });

  const matchingId = activeTooltipId === uniqueAriaId;
  useEffect(() => {
    setContentVisible(matchingId);
  }, [matchingId]);

  const openTooltip = () => setActiveTooltipId(uniqueAriaId);
  const closeTooltip = () => setActiveTooltipId(null);
  useClickOutside(containerRef, closeTooltip);

  const TriggerElement = 'button';

  return (
    <aside ref={containerRef} className={cn(style.trigger, containerClass)}>
      <TriggerElement
        role="button"
        aria-describedby={uniqueAriaId}
        aria-controls={uniqueAriaId}
        aria-expanded={contentVisible}
        type="button"
        onMouseEnter={openTooltip}
        onClick={openTooltip}
        onFocus={openTooltip}
        onBlur={closeTooltip}
        className={cn('btn p-0.5', triggerButtonClass)}
        {...restProps}
      >
        {triggerButtonContent}
      </TriggerElement>
      <CSSTransition
        in={contentVisible}
        timeout={3000}
        classNames="fadeInOut"
        unmountOnExit
        nodeRef={transitionRef}
      >
        <div
          ref={transitionRef}
          id={uniqueAriaId}
          role="tooltip"
          tabIndex={0}
          aria-hidden={!contentVisible}
          aria-details={uniqueAriaId}
          style={tooltipStyle || undefined}
          className={cn(style.tooltip, style[position], tooltipClass, {
            'text-center': centered,
          })}
        >
          {children}
        </div>
      </CSSTransition>
    </aside>
  );
};

Tooltip.propTypes = {
  ariaId: PropTypes.string.isRequired,
  clamp: PropTypes.shape({
    enabled: PropTypes.bool,
    transform: PropTypes.string,
  }),
  triggerButtonClass: PropTypes.string,
  tooltipClass: PropTypes.string,
  position: PropTypes.oneOf(Object.values(POSITION_TYPES)),
  centered: PropTypes.bool,
  containerClass: PropTypes.string,
  triggerButtonContent: PropTypes.node,
  trackArrow: PropTypes.bool,
  children: PropTypes.node,
};

export default Tooltip;
